import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import config from '../config';
import MapPicker from './child/MapPicker';
import ImageCropperModal from './common/ImageCropperModal';
import WorkingHoursEditor, { hasWorkingHours, workingHoursError } from './child/WorkingHoursEditor';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB limit for source, we compress output

const AddVenueLayer = ({ venueId = null, onSuccess, onNavigateTab }) => {
    const { auth, user } = useAuth();
    const isEditMode = !!venueId;
    const draftStorageKey = `hs_venue_draft_${user?.id || 'default'}`;
    
    // Gallery & Features Showcase for edit mode
    const [galleryImages, setGalleryImages] = useState([]);
    
    // Cropper State
    const [cropperState, setCropperState] = useState({
        show: false,
        imageSrc: null,
        initialAspectRatio: 1,
        fixedAspectRatio: null,
        minWidth: 750,
        type: null // 'logo' or 'thumbnail'
    });
    const [formData, setFormData] = useState({
        title: '',
        business_name: '',
        description: '',
        license_number: '',
        phone: '',
        email: '',
        dm_type: 'none',
        dm_value: '',
        business_category: '', // Keep for backward compatibility
        category_ids: [],      // New: Array of selected category IDs
        primary_category_id: null, // New: Primary category ID
        address: {
            street: '',
            city: '',
            state: '',
            zip: '',
            country: ''
        },
        area: '',
        location: null,
        working_hours: null
    });
    const [isLockedForReview, setIsLockedForReview] = useState(false);

    // File State
    const [logo, setLogo] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [thumbnail, setThumbnail] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    
    // Features State
    const [, setFeatures] = useState([]);
    
    // Categories State
    const [categories, setCategories] = useState([]);

    // Fixed list of areas the app filters by
    const [areas, setAreas] = useState([]);

    // Subscription Limits
    const [limits, setLimits] = useState(null);
    const [, setUsage] = useState(null);
    
    // UI State
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(isEditMode);
    const [errors, setErrors] = useState({});
    const [showSubmitConfirmModal, setShowSubmitConfirmModal] = useState(false);

    // Real-Time Address Autocomplete State
    const [activeAddressField, setActiveAddressField] = useState(null);
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const addressDebounceRef = useRef(null);
    const addressSectionRef = useRef(null);

    // Fetch subscription limits
    const fetchLimits = useCallback(async () => {
        try {
            const url = venueId 
                ? `${config.API_BASE_URL}/api/v2/venues/limits?venue_id=${venueId}`
                : `${config.API_BASE_URL}/api/v2/venues/limits`;
            
            const response = await auth.fetch(url);
            const data = await response.json();
            
            if (response.ok) {
                setLimits(data.limits);
                setUsage({
                    gallery: data.limits.gallery.current,
                    blogs: data.limits.blogs.current,
                    features: data.limits.features.current,
                    venues: data.limits.venues.current
                });
            }
        } catch (error) {
            console.error('Error fetching limits:', error);
        }
    }, [auth, venueId]);

    // Fetch categories from API
    const fetchCategories = useCallback(async () => {
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/categories?limit=100`);
            const data = await response.json();
            
            if (response.ok && data.data) {
                setCategories(data.data);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    }, [auth]);

    const fetchAreas = useCallback(async () => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/api/public/areas`);
            const data = await response.json();
            if (response.ok && Array.isArray(data)) {
                setAreas(data);
            }
        } catch (error) {
            console.error('Error fetching areas:', error);
        }
    }, []);

    // Fetch venue data for edit mode
    const fetchVenueData = useCallback(async () => {
        if (!venueId) return;
        
        try {
            setFetchingData(true);
            const response = await auth.fetch(`${config.API_BASE_URL}/api/v2/venues/${venueId}`);
            const data = await response.json();
            
            if (response.ok) {
                // Extract category IDs from response
                const categoryIds = data.categories 
                    ? data.categories.map(c => c.id) 
                    : [];
                const primaryCatId = data.categories 
                    ? data.categories.find(c => c.is_primary)?.id || categoryIds[0] || null
                    : data.primary_category_id || null;

                let parsedLocation = null;
                if (data.location) {
                    try {
                        parsedLocation = typeof data.location === 'string' ? JSON.parse(data.location) : data.location;
                    } catch (e) {
                        parsedLocation = null;
                    }
                }
                if (!parsedLocation?.coordinates) {
                    const lat = parseFloat(data.latitude ?? data.venue_latitude);
                    const lng = parseFloat(data.longitude ?? data.venue_longitude);
                    if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng)) {
                        parsedLocation = { type: 'Point', coordinates: [lng, lat] };
                    }
                }

                setIsLockedForReview(
                    (data.verification_status || 'pending') === 'pending' && !data.owner_edit_unlocked && user?.role !== 'admin'
                );
                setFormData({
                    title: data.title || data.business_name || '',
                    business_name: data.business_name || '',
                    description: data.description || '',
                    license_number: data.license_number || '',
                    phone: data.phone || '',
                    email: data.email || '',
                    business_category: data.business_category || '',
                    dm_type: data.dm_type || 'none',
                    dm_value: data.dm_value || '',
                    category_ids: categoryIds,
                    primary_category_id: primaryCatId,
                    address: {
                        street: data.street || data.address_street || '',
                        city: data.city || data.address_city || '',
                        state: data.state || data.address_state || '',
                        zip: data.zip || data.address_zip || '',
                        country: data.country || data.address_country || ''
                    },
                    area: data.area || '',
                    location: parsedLocation,
                    working_hours: typeof data.working_hours === 'object' ? data.working_hours : null
                });
                
                if (data.logo) setLogoPreview(data.logo);
                if (data.thumbnail) setThumbnailPreview(data.thumbnail);
                if (data.features) setFeatures(data.features);
                if (Array.isArray(data.gallery)) setGalleryImages(data.gallery);
                
                if (data.limits) {
                    setLimits({
                        gallery: { max: data.limits.galleryLimit, current: data.gallery?.length || 0 },
                        blogs: { max: data.limits.blogLimit, current: data.blogs?.length || 0 },
                        features: { max: data.limits.featureLimit, current: data.features?.length || 0 }
                    });
                }
            } else {
                toast.error(data.message || 'Failed to load venue');
            }
        } catch (error) {
            console.error('Error fetching venue:', error);
            toast.error('Failed to load venue data');
        } finally {
            setFetchingData(false);
        }
    }, [auth, venueId, user?.role]);

    useEffect(() => {
        fetchLimits();
        fetchCategories();
        fetchAreas();
        if (isEditMode) {
            fetchVenueData();
        } else {
            (async () => {
                try {
                    const response = await auth.fetch(`${config.API_BASE_URL}/api/v2/venues/draft`);
                    const result = response.ok ? await response.json() : null;
                    // Drafts saved before they moved to the server are still in this browser
                    const parsed = result?.draft || JSON.parse(localStorage.getItem(draftStorageKey) || 'null');
                    if (parsed && typeof parsed === 'object') {
                        setFormData(prev => ({
                            ...prev,
                            ...parsed,
                            address: {
                                ...prev.address,
                                ...(parsed.address || {})
                            }
                        }));
                    }
                } catch (e) {
                    console.warn('Could not load saved venue draft:', e);
                }
            })();
        }
    }, [auth, fetchLimits, fetchCategories, fetchAreas, fetchVenueData, isEditMode, draftStorageKey]);

    // Close address autocomplete and confirmation modal on ESC or outside click
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setAddressSuggestions([]);
                setActiveAddressField(null);
                setShowSubmitConfirmModal(false);
            }
        };
        const handleMouseDown = (e) => {
            if (addressSectionRef.current && !addressSectionRef.current.contains(e.target)) {
                setAddressSuggestions([]);
                setActiveAddressField(null);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handleMouseDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleMouseDown);
        };
    }, []);

    // Clean up object URLs on unmount
    useEffect(() => {
        return () => {
            if (cropperState.imageSrc) {
                URL.revokeObjectURL(cropperState.imageSrc);
            }
        };
    }, [cropperState.imageSrc]);

    // Handle file selection - Opens Cropper
    const handleFileChange = (e, type) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            toast.error('Only JPG, PNG, and WebP formats are allowed');
            e.target.value = '';
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
             toast.error(`File size must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
             e.target.value = '';
             return;
        }

        const objectUrl = URL.createObjectURL(file);
        const isLogo = type === 'logo';

        setCropperState({
            show: true,
            imageSrc: objectUrl,
            type: type,
            initialAspectRatio: isLogo ? 1 : 3/2,
            fixedAspectRatio: isLogo ? 1 : 3/2, // Enforce locked ratio: 1:1 for Logo, 3:2 for Thumbnail/Header
            minWidth: 750
        });
        
        // Reset input value to allow re-selecting same file
        e.target.value = '';
    };

    const handleCropperHide = useCallback(() => {
        if (cropperState.imageSrc) {
            URL.revokeObjectURL(cropperState.imageSrc);
        }
        setCropperState(prev => ({ ...prev, show: false, imageSrc: null }));
    }, [cropperState.imageSrc]);

    const handleCropComplete = useCallback((croppedFile, croppedPreview) => {
        if (cropperState.type === 'logo') {
            setLogo(croppedFile);
            setLogoPreview(croppedPreview);
        } else {
            setThumbnail(croppedFile);
            setThumbnailPreview(croppedPreview);
        }
        // Modal closes automatically via onHide prop in ImageCropperModal calls handleCropperHide
    }, [cropperState.type]);

    // Real-time address autocomplete search
    const fetchAddressSuggestions = useCallback(async (field, queryValue, currentAddress) => {
        const q = (queryValue || '').trim();
        if (q.length < 2) {
            setAddressSuggestions([]);
            return;
        }
        try {
            const contextParts = [q];
            if (field !== 'city' && currentAddress?.city) contextParts.push(currentAddress.city);
            if (field !== 'country' && currentAddress?.country) contextParts.push(currentAddress.country);
            const searchQuery = contextParts.join(', ');
            const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&addressdetails=1&limit=5&accept-language=en`;
            const res = await fetch(url, { headers: { Accept: 'application/json' } });
            if (!res.ok) return;
            const data = await res.json();
            if (Array.isArray(data)) {
                const mapped = data.map(item => {
                    const a = item.address || {};
                    const streetParts = [
                        a.house_number,
                        a.road || a.pedestrian || a.footway || a.suburb || a.neighbourhood
                    ].filter(Boolean);
                    return {
                        display_name: item.display_name,
                        lat: parseFloat(item.lat),
                        lng: parseFloat(item.lon),
                        address: {
                            street: streetParts.join(' ') || item.name || q,
                            city: a.city || a.town || a.village || a.municipality || a.county || currentAddress?.city || '',
                            state: a.state || a.region || currentAddress?.state || '',
                            zip: a.postcode || currentAddress?.zip || '',
                            country: a.country || currentAddress?.country || ''
                        }
                    };
                });
                setAddressSuggestions(mapped);
            }
        } catch (err) {
            // ignore network errors during typing
        }
    }, []);

    const handleSelectAddressSuggestion = (suggestion) => {
        setAddressSuggestions([]);
        setActiveAddressField(null);
        setFormData(prev => ({
            ...prev,
            location: (!isNaN(suggestion.lat) && !isNaN(suggestion.lng))
                ? { type: 'Point', coordinates: [suggestion.lng, suggestion.lat] }
                : prev.location,
            address: {
                street: suggestion.address.street || prev.address.street,
                city: suggestion.address.city || prev.address.city,
                state: suggestion.address.state || prev.address.state,
                zip: suggestion.address.zip || prev.address.zip,
                country: suggestion.address.country || prev.address.country
            }
        }));
    };

    // Handle form field changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        
        if (name.startsWith('address.')) {
            const addressField = name.split('.')[1];
            const nextAddress = { ...formData.address, [addressField]: value };
            setFormData(prev => ({
                ...prev,
                address: nextAddress
            }));

            if (['street', 'city', 'state', 'country'].includes(addressField)) {
                setActiveAddressField(addressField);
                if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
                if (value.trim().length >= 2) {
                    addressDebounceRef.current = setTimeout(() => {
                        fetchAddressSuggestions(addressField, value, nextAddress);
                    }, 280);
                } else {
                    setAddressSuggestions([]);
                }
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    // Handle location selection from map
    const handleLocationSelect = (location, address) => {
        setFormData(prev => ({
            ...prev,
            location: {
                type: 'Point',
                coordinates: [location.lng, location.lat]
            },
            address: address ? {
                street: address.street !== undefined ? address.street : prev.address.street,
                city: address.city !== undefined ? address.city : prev.address.city,
                state: address.state !== undefined ? address.state : prev.address.state,
                zip: address.zip !== undefined ? address.zip : prev.address.zip,
                country: address.country !== undefined ? address.country : prev.address.country
            } : prev.address
        }));
    };

    // Save draft on the server so the user can continue later from any device
    const handleSaveDraft = async () => {
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/v2/venues/draft`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ draft: formData })
            });
            if (!response.ok) throw new Error('Failed to save draft');
            toast.success('Draft saved. You can return and continue anytime.');
        } catch (err) {
            toast.error('Failed to save draft');
        }
    };

    // Validate form - returns errors object or null if valid
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.title?.trim()) {
            newErrors.title = 'Title is required';
        } else if (formData.title.length > 120) {
            newErrors.title = 'Title must not exceed 120 characters';
        }
        
        if (formData.description && formData.description.length > 2000) {
            newErrors.description = 'Description must not exceed 2000 characters';
        }
        
        // Validate DM Options
        if (formData.dm_type && formData.dm_type !== 'none' && !formData.dm_value?.trim()) {
            newErrors.dm_value = formData.dm_type === 'whatsapp' 
                ? 'WhatsApp number is required' 
                : 'Instagram username is required';
        }
        
        if (!formData.address.street?.trim()) {
            newErrors['address.street'] = 'Street address is required';
        }
        if (!formData.address.city?.trim()) {
            newErrors['address.city'] = 'City is required';
        }
        if (!formData.address.country?.trim()) {
            newErrors['address.country'] = 'Country is required';
        }
        
        // Check venue limit for new venues (admins are exempt)
        if (!isEditMode && limits?.venues && user?.role !== 'admin') {
            if (limits.venues.current >= limits.venues.max) {
                newErrors.general = `You have reached the maximum number of venues (${limits.venues.max}) allowed by your plan.`;
            }
        }
        
        setErrors(newErrors);
        
        // Return the errors for immediate use
        if (Object.keys(newErrors).length > 0) {
            return newErrors;
        }
        return null;
    };

    const executeVenueSubmit = async () => {
        setShowSubmitConfirmModal(false);
        setLoading(true);
        
        try {
            const formDataToSend = new FormData();
            
            // Add text fields
            formDataToSend.append('title', formData.title);
            formDataToSend.append('business_name', formData.business_name || formData.title);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('license_number', formData.license_number);
            formDataToSend.append('phone', formData.phone);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('business_category', formData.business_category);
            formDataToSend.append('dm_type', formData.dm_type);
            formDataToSend.append('dm_value', formData.dm_value);

            // Add pricing & entertainment fields
            if (formData.price_level) formDataToSend.append('price_level', formData.price_level);
            if (formData.average_price_for_two) formDataToSend.append('average_price_for_two', formData.average_price_for_two);
            if (formData.currency) formDataToSend.append('currency', formData.currency);
            if (formData.entertainment_type) formDataToSend.append('entertainment_type', formData.entertainment_type);
            if (formData.entertainment_name) formDataToSend.append('entertainment_name', formData.entertainment_name);
            if (formData.entertainment_description) formDataToSend.append('entertainment_description', formData.entertainment_description);
            if (formData.entertainment_price !== undefined && formData.entertainment_price !== '') {
                formDataToSend.append('entertainment_price', formData.entertainment_price);
            }
            if (formData.entertainment_schedule) formDataToSend.append('entertainment_schedule', formData.entertainment_schedule);
            if (hasWorkingHours(formData.working_hours)) {
                formDataToSend.append('working_hours', JSON.stringify(formData.working_hours));
            } else if (isEditMode) {
                formDataToSend.append('working_hours', 'null');
            }
            
            // Add category data
            if (formData.category_ids && formData.category_ids.length > 0) {
                formDataToSend.append('category_ids', JSON.stringify(formData.category_ids));
                formDataToSend.append('primary_category_id', formData.primary_category_id || formData.category_ids[0]);
            }
            
            // Add structured address as JSON
            formDataToSend.append('address', JSON.stringify(formData.address));
            formDataToSend.append('area', formData.area || '');

            // Add location as JSON
            if (formData.location) {
                formDataToSend.append('location', JSON.stringify(formData.location));
            }
            
            // Add files
            if (logo) formDataToSend.append('logo', logo);
            if (thumbnail) formDataToSend.append('thumbnail', thumbnail);
            
            const url = isEditMode 
                ? `${config.API_BASE_URL}/api/v2/venues/${venueId}`
                : `${config.API_BASE_URL}/api/v2/venues`;
            
            const response = await auth.fetch(url, {
                method: isEditMode ? 'PUT' : 'POST',
                body: formDataToSend,
                headers: {} // Let browser set Content-Type for FormData
            });
            
            // Check content type before parsing
            const contentType = response.headers.get('content-type');
            let data;
            if (contentType && contentType.includes('application/json')) {
                data = await response.json();
            } else {
                const text = await response.text();
                console.error('Server returned non-JSON response:', text);
                data = { error: 'SERVER_ERROR', message: 'Server returned an invalid response' };
            }
            
            if (response.ok) {
                if (!isEditMode) {
                    try { localStorage.removeItem(draftStorageKey); } catch (_) {}
                    auth.fetch(`${config.API_BASE_URL}/api/v2/venues/draft`, { method: 'DELETE' }).catch(() => {});
                }
                toast.success(isEditMode ? 'Venue updated successfully!' : 'Venue submitted for verification!');
                if (onSuccess) onSuccess(data);
            } else {
                if (data.error === 'SUBSCRIPTION_LIMIT_EXCEEDED') {
                    toast.error(data.message);
                } else if (data.errors) {
                    const errorMessages = data.errors.map(e => `${e.field}: ${e.message}`).join(', ');
                    toast.error(errorMessages);
                } else {
                    toast.error(data.message || 'Failed to save venue');
                }
            }
        } catch (error) {
            console.error('Error saving venue:', error);
            toast.error('An error occurred while saving the venue');
        } finally {
            setLoading(false);
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const validationErrors = validateForm();
        if (validationErrors) {
            // Show specific error messages
            const errorList = Object.values(validationErrors);
            if (errorList.length === 1) {
                toast.error(errorList[0]);
            } else {
                toast.error(`Missing required fields: ${errorList.slice(0, 3).join(', ')}${errorList.length > 3 ? '...' : ''}`);
            }
            return;
        }

        const hoursError = workingHoursError(formData.working_hours);
        if (hoursError) {
            toast.error(hoursError);
            return;
        }

        if (!isEditMode) {
            setShowSubmitConfirmModal(true);
            return;
        }

        await executeVenueSubmit();
    };

    // Calculate remaining slots
    // const getRemainingSlots = (type) => {
    //     if (!limits || !limits[type]) return null;
    //     return Math.max(0, limits[type].max - limits[type].current);
    // };

    if (fetchingData) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="hs-card p-0">
            <div className="p-20 border-bottom bg-white radius-top-12">
                <h5 className="text-lg fw-bold text-dark mb-0">
                    {isEditMode ? 'Edit Venue Profile' : 'Add New Venue'}
                </h5>
                <span className="text-xs text-secondary-light">
                    {isEditMode ? 'Modify venue details, branding, location, and categories' : 'Enter basic venue details to get started. You can upload full gallery media, menu items, and amenities right after creating.'}
                </span>
            </div>
            
            <div className="p-24">
                {isLockedForReview && (
                    <div className="alert alert-warning radius-8 mb-24" role="alert">
                        This venue is under review, so its details can't be edited right now. If you need to change something, contact HubScroll and an admin can reopen it for you.
                    </div>
                )}
                {/* Subscription Limits Banner */}
                {limits && (
                    <div className="p-16 border rounded-12 bg-light mb-24 d-flex flex-wrap gap-3 align-items-center">
                        <div className="d-flex align-items-center flex-wrap gap-2">
                            <span className="text-sm fw-bold text-dark">Plan Quota & Limits:</span>
                            <span className="hs-badge hs-badge-info">
                                Venues: {limits.venues?.current || 0} / {limits.venues?.max || 0}
                            </span>
                            {limits.gallery && (
                                <span className="hs-badge hs-badge-info">
                                    Gallery: {limits.gallery?.current || 0} / {limits.gallery?.max || 0}
                                </span>
                            )}
                            {limits.blogs && (
                                <span className="hs-badge hs-badge-info">
                                    Blogs: {limits.blogs?.current || 0} / {limits.blogs?.max || 0}
                                </span>
                            )}
                            {limits.features && (
                                <span className="hs-badge hs-badge-info">
                                    Features: {limits.features?.current || 0} / {limits.features?.max || 0}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {errors.general && (
                    <div className="alert alert-danger mb-24">
                        <Icon icon="mdi:alert-circle" className="me-2" />
                        {errors.general}
                    </div>
                )}
                
                <form onSubmit={handleSubmit}>
                    {/* Basic Information */}
                    <div className="row gy-3 mb-24">
                        <div className="col-12">
                            <h6 className="text-md fw-semibold mb-16 border-bottom pb-8">
                                Basic Information
                            </h6>
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Title <span className="text-danger">*</span>
                            </label>
                            <div className="hs-field-with-counter">
                                <input
                                    type="text"
                                    className={`form-control radius-8 ${errors.title ? 'is-invalid' : ''}`}
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    maxLength={120}
                                />
                                <span className="hs-char-counter">{formData.title.length}/120</span>
                            </div>
                            {errors.title && <div className="invalid-feedback d-block">{errors.title}</div>}
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Venue Business Name
                            </label>
                            <input
                                type="text"
                                className="form-control radius-8"
                                name="business_name"
                                value={formData.business_name}
                                onChange={handleChange}
                            />
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Categories
                            </label>
                            <div className="border radius-8 p-12 bg-base" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {categories.length === 0 ? (
                                    <p className="text-secondary-light mb-0 text-sm">Loading categories...</p>
                                ) : (
                                    categories.map((category) => (
                                        <div key={category.id} className="form-check d-flex align-items-center gap-8 mb-8">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id={`cat-${category.id}`}
                                                checked={formData.category_ids.includes(category.id)}
                                                onChange={(e) => {
                                                    const catId = category.id;
                                                    if (e.target.checked) {
                                                        // Add category
                                                        const newIds = [...formData.category_ids, catId];
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            category_ids: newIds,
                                                            business_category: category.slug || category.name,
                                                            // Set as primary if first
                                                            primary_category_id: prev.primary_category_id || catId
                                                        }));
                                                    } else {
                                                        // Remove category
                                                        const newIds = formData.category_ids.filter(id => id !== catId);
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            category_ids: newIds,
                                                            // Update primary if removed
                                                            primary_category_id: prev.primary_category_id === catId 
                                                                ? newIds[0] || null 
                                                                : prev.primary_category_id
                                                        }));
                                                    }
                                                }}
                                            />
                                            <label className="form-check-label text-sm" htmlFor={`cat-${category.id}`}>
                                                {category.name}
                                            </label>
                                            {formData.category_ids.includes(category.id) && (
                                                formData.primary_category_id === category.id ? (
                                                    <span
                                                        className="hs-badge hs-badge-info py-2 px-8 ms-auto text-xs fw-medium"
                                                        title="Primary category"
                                                    >
                                                        ★ Primary
                                                    </span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm py-1 px-8 ms-auto text-secondary fw-normal border border-neutral-200 bg-white radius-6"
                                                        style={{ fontSize: '12px' }}
                                                        onClick={() => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                primary_category_id: category.id,
                                                                business_category: category.slug || category.name
                                                            }));
                                                        }}
                                                        title="Set as primary category"
                                                    >
                                                        Set as Primary
                                                    </button>
                                                )
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                            {formData.category_ids.length > 0 && (
                                <small className="text-secondary-light mt-4 d-block">
                                    {formData.category_ids.length} selected | Primary: {categories.find(c => c.id === formData.primary_category_id)?.name || 'None'}
                                </small>
                            )}
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                License Number
                            </label>
                            <input
                                type="text"
                                className="form-control radius-8"
                                name="license_number"
                                value={formData.license_number}
                                onChange={handleChange}
                            />
                        </div>
                        
                        <div className="col-12">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Description
                            </label>
                            <div className="hs-field-with-counter">
                                <textarea
                                    className={`form-control radius-8 ${errors.description ? 'is-invalid' : ''}`}
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows={4}
                                    maxLength={2000}
                                />
                                <span className="hs-char-counter">{formData.description.length}/2000</span>
                            </div>
                            {errors.description && <div className="invalid-feedback d-block">{errors.description}</div>}
                        </div>
                    </div>
                    
                    {/* Media Upload */}
                    <div className="row gy-3 mb-24">
                        <div className="col-12">
                            <h6 className="text-md fw-semibold mb-16 border-bottom pb-8">
                                Media
                            </h6>
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Logo
                                <small className="ms-2" style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 400 }}>(Max 2MB, JPG/PNG/WebP)</small>
                            </label>
                            <div className="d-flex align-items-center gap-3">
                                {logoPreview && (
                                    <img
                                        src={logoPreview.startsWith('data:') || logoPreview.startsWith('blob:') ? logoPreview : `${config.API_BASE_URL}/${logoPreview}`}
                                        alt="Logo preview"
                                        className="w-80-px h-80-px rounded-circle object-fit-cover border"
                                    />
                                )}
                                <div className="flex-grow-1">
                                    <input
                                        type="file"
                                        className="form-control radius-8"
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={(e) => handleFileChange(e, 'logo')}
                                    />
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Thumbnail
                                <small className="ms-2" style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 400 }}>(Max 10MB source, optimized output)</small>
                            </label>
                            <div className="d-flex align-items-center gap-3">
                                {thumbnailPreview && (
                                    <img
                                        src={thumbnailPreview.startsWith('data:') || thumbnailPreview.startsWith('blob:') ? thumbnailPreview : `${config.API_BASE_URL}/${thumbnailPreview}`}
                                        alt="Thumbnail preview"
                                        className="w-120-px h-80-px rounded-8 object-fit-cover border"
                                    />
                                )}
                                <div className="flex-grow-1">
                                    <input
                                        type="file"
                                        className="form-control radius-8"
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={(e) => handleFileChange(e, 'thumbnail')}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Venue Photo Gallery Showcase (Edit mode only) */}
                        {isEditMode && (
                            <div className="col-12 mt-12">
                                <div className="p-16 radius-12 bg-neutral-50 border border-neutral-200">
                                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-12">
                                        <div>
                                            <div className="d-flex align-items-center gap-2">
                                                <Icon icon="solar:gallery-wide-bold" className="text-primary-600 text-xl" />
                                                <h6 className="text-sm fw-bold text-neutral-800 mb-0">Venue Photo Gallery</h6>
                                                <span className="badge bg-primary-50 text-primary-600 px-8 py-2 radius-4 text-xs fw-semibold border border-primary-100">
                                                    {galleryImages.length} {galleryImages.length === 1 ? 'Photo' : 'Photos'}
                                                </span>
                                            </div>
                                            <p className="text-xs text-neutral-500 mt-4 mb-0">
                                                High-resolution photos increase user reservations and engagement on HubScroll.
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => onNavigateTab ? onNavigateTab('gallery') : null}
                                            className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2 radius-8 fw-semibold px-16 py-8"
                                        >
                                            <Icon icon="solar:upload-track-2-bold" className="text-base" />
                                            Manage Full Gallery
                                        </button>
                                    </div>

                                    {galleryImages.length > 0 ? (
                                        <div className="d-flex align-items-center gap-12 overflow-x-auto pb-4">
                                            {galleryImages.slice(0, 6).map((img, idx) => {
                                                const url = img.image_url || img.thumbnail_url || img.url;
                                                const fullUrl = url?.startsWith('http') ? url : `${config.API_BASE_URL}/${url?.replace(/^\//, '')}`;
                                                return (
                                                    <div 
                                                        key={img.id || idx} 
                                                        className="position-relative radius-8 overflow-hidden border border-neutral-200 flex-shrink-0"
                                                        style={{ width: '90px', height: '65px', cursor: 'pointer' }}
                                                        onClick={() => onNavigateTab ? onNavigateTab('gallery') : null}
                                                        title={img.caption || 'Click to manage gallery'}
                                                    >
                                                        <img
                                                            src={fullUrl}
                                                            alt={img.caption || `Gallery ${idx + 1}`}
                                                            className="w-100 h-100 object-fit-cover"
                                                        />
                                                    </div>
                                                );
                                            })}
                                            {galleryImages.length > 6 && (
                                                <button
                                                    type="button"
                                                    onClick={() => onNavigateTab ? onNavigateTab('gallery') : null}
                                                    className="d-flex flex-column align-items-center justify-content-center radius-8 border border-dashed border-neutral-300 bg-white flex-shrink-0 text-neutral-600"
                                                    style={{ width: '90px', height: '65px' }}
                                                >
                                                    <span className="text-xs fw-bold">+{galleryImages.length - 6}</span>
                                                    <span className="text-2xs text-neutral-500">More</span>
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-between p-12 radius-8 bg-white border border-dashed border-neutral-300">
                                            <div className="d-flex align-items-center gap-2 text-neutral-500 text-xs">
                                                <Icon icon="solar:info-circle-linear" className="text-base" />
                                                <span>
                                                    No gallery photos uploaded yet. Showcase your interior, ambience, and outdoor areas.
                                                </span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => onNavigateTab ? onNavigateTab('gallery') : null}
                                                className="btn btn-primary btn-sm py-4 px-12 text-xs radius-6"
                                            >
                                                Upload Photos
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Contact Information */}
                    <div className="row gy-3 mb-24">
                        <div className="col-12">
                            <h6 className="text-md fw-semibold mb-16 border-bottom pb-8">
                                Contact Information
                            </h6>
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Phone
                            </label>
                            <input
                                type="tel"
                                className="form-control radius-8"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="+1 234 567 8900"
                            />
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Email
                            </label>
                            <input
                                type="email"
                                className="form-control radius-8"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="venue@example.com"
                            />
                        </div>
                    </div>
                    
                    {/* Direct Messaging Options */}
                    <div className="row gy-3 mb-24">
                        <div className="col-12">
                            <h6 className="text-md fw-semibold mb-16 border-bottom pb-8">
                                Direct Messaging Options
                            </h6>
                        </div>
                        
                        <div className="col-12">
                            <div className="d-flex flex-wrap gap-4">
                                <div className="d-flex align-items-center gap-2 me-3">
                                    <input
                                        className="form-check-input mt-0"
                                        type="radio"
                                        name="dm_type"
                                        id="dm_none"
                                        value="none"
                                        checked={formData.dm_type === 'none'}
                                        onChange={() => {
                                            setFormData(prev => ({ ...prev, dm_type: 'none', dm_value: '' }));
                                        }}
                                    />
                                    <label className="form-check-label cursor-pointer mb-0" htmlFor="dm_none">
                                        None
                                    </label>
                                </div>
                                <div className="d-flex align-items-center gap-2 me-3">
                                    <input
                                        className="form-check-input mt-0"
                                        type="radio"
                                        name="dm_type"
                                        id="dm_whatsapp"
                                        value="whatsapp"
                                        checked={formData.dm_type === 'whatsapp'}
                                        onChange={() => {
                                            setFormData(prev => ({ ...prev, dm_type: 'whatsapp', dm_value: '' }));
                                        }}
                                    />
                                    <label className="form-check-label d-flex align-items-center gap-2 cursor-pointer mb-0" htmlFor="dm_whatsapp">
                                        <Icon icon="logos:whatsapp-icon" width="20" /> WhatsApp
                                    </label>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <input
                                        className="form-check-input mt-0"
                                        type="radio"
                                        name="dm_type"
                                        id="dm_instagram"
                                        value="instagram"
                                        checked={formData.dm_type === 'instagram'}
                                        onChange={() => {
                                            setFormData(prev => ({ ...prev, dm_type: 'instagram', dm_value: '' }));
                                        }}
                                    />
                                    <label className="form-check-label d-flex align-items-center gap-2 cursor-pointer mb-0" htmlFor="dm_instagram">
                                        <Icon icon="skill-icons:instagram" width="20" /> Instagram
                                    </label>
                                </div>
                            </div>
                        </div>

                        {formData.dm_type !== 'none' && (
                            <div className="col-md-6">
                                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                    {formData.dm_type === 'whatsapp' ? 'WhatsApp Number' : 'Instagram Username'} <span className="text-danger">*</span>
                                </label>
                                <div className="input-group">
                                    <span className="input-group-text bg-base">
                                        <Icon 
                                            icon={formData.dm_type === 'whatsapp' ? 'ph:phone' : 'ph:instagram-logo'} 
                                            className="text-xl text-secondary-light" 
                                        />
                                    </span>
                                    <input
                                        type="text"
                                        className={`form-control radius-8 ${errors.dm_value ? 'is-invalid' : ''}`}
                                        name="dm_value"
                                        value={formData.dm_value}
                                        onChange={handleChange}
                                        placeholder={formData.dm_type === 'whatsapp' ? '+971 50 123 4567' : 'nexusdesignstudios'}
                                    />
                                    {errors.dm_value && <div className="invalid-feedback">{errors.dm_value}</div>}
                                </div>
                                <div className="form-text mt-1" style={{ fontSize: '12px', color: '#94a3b8' }}>
                                    {formData.dm_type === 'whatsapp' 
                                        ? 'Include country code (e.g. +971).' 
                                        : 'Enter your Instagram username or profile link.'}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Working Hours */}
                    <div className="row gy-3 mb-24">
                        <div className="col-12">
                            <h6 className="text-md fw-semibold mb-16 border-bottom pb-8">
                                Working Hours
                            </h6>
                            <WorkingHoursEditor
                                value={formData.working_hours}
                                onChange={(working_hours) => setFormData(prev => ({ ...prev, working_hours }))}
                            />
                        </div>
                    </div>

                    {/* Address */}
                    <div ref={addressSectionRef} className="row gy-3 mb-24">
                        <div className="col-12">
                            <h6 className="text-md fw-semibold mb-16 border-bottom pb-8">
                                Address
                            </h6>
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Area
                            </label>
                            <select
                                className="form-select radius-8"
                                name="area"
                                value={formData.area}
                                onChange={handleChange}
                            >
                                <option value="">Select an area</option>
                                {areas.map(area => (
                                    <option key={area.slug} value={area.slug}>{area.name}</option>
                                ))}
                            </select>
                            <small className="text-secondary-light text-xs">Used by the app's Explore area filter.</small>
                        </div>
                        <div className="col-md-6 d-none d-md-block" />

                        <div className="col-md-6 position-relative">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                City <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                className={`form-control radius-8 ${errors['address.city'] ? 'is-invalid' : ''}`}
                                name="address.city"
                                value={formData.address.city}
                                onChange={handleChange}
                                autoComplete="off"
                            />
                            {activeAddressField === 'city' && addressSuggestions.length > 0 && (
                                <div
                                    className="bg-white border radius-8 shadow-sm position-absolute start-0 end-0 mt-1"
                                    style={{ zIndex: 1100, maxHeight: '220px', overflowY: 'auto' }}
                                >
                                    {addressSuggestions.map((s, idx) => (
                                        <div
                                            key={idx}
                                            className="px-14 py-8 border-bottom cursor-pointer text-xs text-dark d-flex align-items-center gap-2"
                                            onClick={() => handleSelectAddressSuggestion(s)}
                                        >
                                            <Icon icon="solar:map-point-linear" className="text-primary-600 flex-shrink-0" />
                                            <span className="text-truncate">{s.display_name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {errors['address.city'] && <div className="invalid-feedback">{errors['address.city']}</div>}
                        </div>
                        
                        <div className="col-md-6 position-relative">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                State / Province
                            </label>
                            <input
                                type="text"
                                className="form-control radius-8"
                                name="address.state"
                                value={formData.address.state}
                                onChange={handleChange}
                                autoComplete="off"
                            />
                            {activeAddressField === 'state' && addressSuggestions.length > 0 && (
                                <div
                                    className="bg-white border radius-8 shadow-sm position-absolute start-0 end-0 mt-1"
                                    style={{ zIndex: 1100, maxHeight: '220px', overflowY: 'auto' }}
                                >
                                    {addressSuggestions.map((s, idx) => (
                                        <div
                                            key={idx}
                                            className="px-14 py-8 border-bottom cursor-pointer text-xs text-dark d-flex align-items-center gap-2"
                                            onClick={() => handleSelectAddressSuggestion(s)}
                                        >
                                            <Icon icon="solar:map-point-linear" className="text-primary-600 flex-shrink-0" />
                                            <span className="text-truncate">{s.display_name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="col-12 position-relative">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Street <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                className={`form-control radius-8 ${errors['address.street'] ? 'is-invalid' : ''}`}
                                name="address.street"
                                value={formData.address.street}
                                onChange={handleChange}
                                autoComplete="off"
                            />
                            {activeAddressField === 'street' && addressSuggestions.length > 0 && (
                                <div
                                    className="bg-white border radius-8 shadow-sm position-absolute start-0 end-0 mt-1"
                                    style={{ zIndex: 1100, maxHeight: '220px', overflowY: 'auto' }}
                                >
                                    {addressSuggestions.map((s, idx) => (
                                        <div
                                            key={idx}
                                            className="px-14 py-8 border-bottom cursor-pointer text-xs text-dark d-flex align-items-center gap-2"
                                            onClick={() => handleSelectAddressSuggestion(s)}
                                        >
                                            <Icon icon="solar:map-point-linear" className="text-primary-600 flex-shrink-0" />
                                            <span className="text-truncate">{s.display_name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {errors['address.street'] && <div className="invalid-feedback">{errors['address.street']}</div>}
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                ZIP / Postal Code
                            </label>
                            <input
                                type="text"
                                className="form-control radius-8"
                                name="address.zip"
                                value={formData.address.zip}
                                onChange={handleChange}
                            />
                        </div>
                        
                        <div className="col-md-6 position-relative">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Country <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                className={`form-control radius-8 ${errors['address.country'] ? 'is-invalid' : ''}`}
                                name="address.country"
                                value={formData.address.country}
                                onChange={handleChange}
                                autoComplete="off"
                            />
                            {activeAddressField === 'country' && addressSuggestions.length > 0 && (
                                <div
                                    className="bg-white border radius-8 shadow-sm position-absolute start-0 end-0 mt-1"
                                    style={{ zIndex: 1100, maxHeight: '220px', overflowY: 'auto' }}
                                >
                                    {addressSuggestions.map((s, idx) => (
                                        <div
                                            key={idx}
                                            className="px-14 py-8 border-bottom cursor-pointer text-xs text-dark d-flex align-items-center gap-2"
                                            onClick={() => handleSelectAddressSuggestion(s)}
                                        >
                                            <Icon icon="solar:map-point-linear" className="text-primary-600 flex-shrink-0" />
                                            <span className="text-truncate">{s.display_name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {errors['address.country'] && <div className="invalid-feedback">{errors['address.country']}</div>}
                        </div>
                    </div>
                    
                    {/* Map Location */}
                    <div className="row gy-3 mb-24">
                        <div className="col-12">
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 border-bottom pb-8 mb-8">
                                <h6 className="text-md fw-semibold mb-0 d-flex align-items-center gap-2">
                                    <Icon icon="logos:google-maps" className="text-lg" />
                                    <span>Venue Map Location</span>
                                </h6>
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        color: '#0369a1',
                                        background: '#f0f9ff',
                                        border: '1px solid #bae6fd',
                                        padding: '4px 10px',
                                        borderRadius: '999px'
                                    }}
                                >
                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#16a34a' }} />
                                    Google Maps Live Search &amp; Link Resolver
                                </span>
                            </div>
                            <p className="text-secondary-light text-sm mb-12">
                                Click on the map to set the venue location. Address fields will be filled automatically and can be edited if needed.
                            </p>
                        </div>
                        
                        <div className="col-12">
                            <div
                                style={{
                                    height: '760px',
                                    borderRadius: '12px',
                                    border: '1px solid #cbd5e1',
                                    boxShadow: '0 4px 16px -4px rgba(15, 23, 42, 0.06)',
                                    overflow: 'hidden'
                                }}
                            >
                                <MapPicker
                                    initialLocation={formData.location?.coordinates 
                                        ? { lat: formData.location.coordinates[1], lng: formData.location.coordinates[0] }
                                        : null
                                    }
                                    initialAddress={formData.address}
                                    selectedCity={formData.address?.city || ''}
                                    onLocationSelect={handleLocationSelect}
                                />
                            </div>
                        </div>
                    </div>
                    
                    {/* Submit Button Bar */}
                    <div className="d-flex justify-content-end gap-3 pt-20 border-top mt-24 flex-wrap">
                        <button
                            type="button"
                            className="hs-btn-secondary px-24"
                            onClick={() => window.history.back()}
                        >
                            Cancel
                        </button>
                        {!isEditMode && (
                            <button
                                type="button"
                                className="hs-btn-outline-primary px-24 d-inline-flex align-items-center gap-2"
                                onClick={handleSaveDraft}
                                disabled={loading}
                            >
                                <Icon icon="solar:bookmark-linear" className="text-lg" />
                                <span>Save Draft</span>
                            </button>
                        )}
                        <button
                            type="submit"
                            className="hs-btn-primary px-24 d-inline-flex align-items-center gap-2"
                            disabled={loading || isLockedForReview || (limits?.venues && !isEditMode && limits.venues.current >= limits.venues.max)}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" />
                                    <span>{isEditMode ? 'Updating Venue...' : 'Submitting...'}</span>
                                </>
                            ) : (
                                <>
                                    <Icon icon={isEditMode ? "solar:disk-bold" : "solar:check-circle-bold"} className="text-lg" />
                                    <span>{isEditMode ? 'Update Venue' : 'Submit for Verification'}</span>
                                </>
                            )}
                        </button>
                    </div>

                    {/* Confirmation Modal Before Submitting for Verification */}
                    {showSubmitConfirmModal && (
                        <div
                            className="modal fade show d-block"
                            style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)', zIndex: 1060 }}
                            onClick={() => setShowSubmitConfirmModal(false)}
                        >
                            <div
                                className="modal-dialog modal-dialog-centered"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="modal-content radius-16 border-0 shadow-lg">
                                    <div className="modal-header border-bottom py-16 px-24">
                                        <h6 className="modal-title fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                            <Icon icon="solar:shield-check-bold" className="text-primary-600 text-xl" />
                                            <span>Submit Venue for Verification</span>
                                        </h6>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={() => setShowSubmitConfirmModal(false)}
                                        />
                                    </div>
                                    <div className="modal-body p-24">
                                        <p className="text-sm text-dark mb-12">
                                            Are you sure you want to submit <strong>{formData.title || 'this venue'}</strong> for verification?
                                        </p>
                                        <div
                                            className="p-14 radius-8 text-xs"
                                            style={{ backgroundColor: '#fffbeb', border: '1px solid #f59e0b', color: '#b45309' }}
                                        >
                                            <strong>Please note:</strong> Once submitted for verification, basic listing details will be locked during review and cannot be edited unless the administrator reopens the venue for changes.
                                        </div>
                                    </div>
                                    <div className="modal-footer border-top py-16 px-24 d-flex justify-content-end gap-2">
                                        <button
                                            type="button"
                                            className="hs-btn-secondary px-20"
                                            onClick={() => setShowSubmitConfirmModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="hs-btn-primary px-20 d-inline-flex align-items-center gap-2"
                                            onClick={executeVenueSubmit}
                                            disabled={loading}
                                        >
                                            <Icon icon="solar:check-circle-bold" className="text-lg" />
                                            <span>Submit for Verification</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Image Cropper Modal */}
                    {cropperState.show && (
                        <ImageCropperModal
                            show={cropperState.show}
                            onHide={handleCropperHide}
                            imageSrc={cropperState.imageSrc}
                            onCropComplete={handleCropComplete}
                            fixedAspectRatio={cropperState.fixedAspectRatio}
                            initialAspectRatio={cropperState.initialAspectRatio}
                            minWidth={cropperState.minWidth}
                        />
                    )}
                </form>
            </div>
        </div>
    );
};

export default AddVenueLayer;
